import {
  ArrowRight,
  BadgeCheck,
  Check,
  Divide,
  DollarSign,
  FileText,
  Gem,
  Heart,
  Shield,
  Star,
  Users,
  X,
} from 'lucide-react';
import { NextSeo, SiteLinksSearchBoxJsonLd } from 'next-seo';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { IndexFAQSection } from '@/components/app';
import { SecondaryButtonMedium } from '@/components/base/Buttons';
import { SecondaryCard } from '@/components/base/Cards';
import { PageBackgroundFull, PageContainer, PageSectionLarge } from '@/components/base/Sections';
import '@/components/base/Typography';
import {
  TypographyHero,
  TypographyLabel,
  TypographyLarge,
  TypographySectionLabel,
  TypographySectionTitle,
  TypographySmall,
  TypographySubtitle,
  TypographyText,
  TypographyTitle,
} from '@/components/base/Typography';
import { DiamondIcon } from '@/components/diamonds/DiamondIcon';
import { PictureAndButton } from '@/components/diamonds/PictureAndButton';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { AVATAR_COLORS, DEFAULT_SHAPES, REVIEW_SOURCES } from '@/constants';

const featuresData = [
  {
    icon: Star,
    title: 'Exceptional craftsmanship',
    description:
      'Masterfully crafted by skilled artisans using traditional techniques and modern precision to create extraordinary pieces.',
  },
  {
    icon: DollarSign,
    title: 'Value assurance',
    description:
      'Every diamond is competitively priced with transparent certification, ensuring exceptional value for your investment.',
  },
  {
    icon: Shield,
    title: 'Lifetime guarantee',
    description:
      'Our comprehensive lifetime warranty covers craftsmanship and authenticity, providing peace of mind for generations.',
  },
  {
    icon: Users,
    title: 'Personal concierge',
    description:
      'Dedicated diamond specialists provide personalized guidance throughout your journey to find the perfect piece.',
  },
];

// Small reusable header to standardize spacing across sections on this page
const SectionHeader = ({
  title,
  subtitle,
  center = true,
}: {
  title: string;
  subtitle?: string | React.ReactNode;
  center?: boolean;
}) => (
  <div className={`${center ? 'text-center' : ''} mb-12 sm:mb-16`}>
    <TypographyTitle className="mb-6 sm:mb-8">{title}</TypographyTitle>
    {subtitle ? (
      <TypographySubtitle className="mx-auto max-w-2xl">{subtitle}</TypographySubtitle>
    ) : null}
  </div>
);

const inspiredData = [
  {
    title: 'Solitaire Rings',
    href: '/rings/solitaire',
    image: '/images/rings/solitaire.png',
  },
  {
    title: 'Pave Rings',
    href: '/rings/pave',
    image: '/images/rings/side-stone.png',
  },
  {
    title: 'Halo Rings',
    href: '/rings/halo',
    image: '/images/rings/halo.png',
  },
  {
    title: 'Cluster Rings',
    href: '/rings/cluster',
    image: '/images/rings/cluster.png',
  },
  {
    title: 'Vintage Rings',
    href: '/rings/vintage',
    image: '/images/rings/vintage.png',
  },
];

const carouselData = [
  {
    id: 'signature-engagement',
    title: 'Signature Engagement Collection',
    description:
      'Masterfully crafted solitaires and halos featuring exceptional center stones, hand-selected for superior brilliance and fire.',
    href: '/rings/solitaire,halo',
    image: '/images/landing/landing-diamonds-3.jpg',
  },
  {
    id: 'artisan-wedding-bands',
    title: 'Artisan Wedding Bands',
    description:
      'Meticulously crafted platinum and gold bands with distinctive details that complement your engagement piece with harmonious elegance.',
    href: '/rings/pave',
    image: '/images/landing/engagement-ring-hand.jpg',
  },
  {
    id: 'certified-diamonds',
    title: 'Extraordinary Diamond Collection',
    description:
      'Rare, GIA-certified diamonds selected for exceptional color, clarity and cut, including remarkable fancy colors and unique shapes.',
    href: '/diamonds',
    image: '/images/landing/landing-diamonds-1.jpg',
  },
  {
    id: 'bespoke-atelier',
    title: 'Bespoke Atelier Experience',
    description:
      'A personalized journey from concept to creation with our master jewelers, resulting in a one-of-a-kind heirloom worthy of your legacy.',
    href: '/diamonds',
    image: '/images/landing/landing-diamonds-2.jpg',
  },
];

// Derive the value props from the Excellence/features section so titles and descriptions stay in sync.

const testimonialsData = [
  {
    name: 'Timothy H.',
    title: 'New York, NY',
    description:
      'The exceptional quality of the diamond exceeded our expectations. Their expert guidance made our engagement moment truly unforgettable.',
  },
  {
    name: 'Alexandra W.',
    title: 'Design Professional',
    description:
      'Their attention to detail and craftsmanship are unmatched in the industry. The custom piece they created became an instant heirloom.',
  },
  {
    name: 'James & Olivia',
    title: 'Recently Engaged',
    description:
      'We appreciated the transparency throughout the entire process. Their certified diamonds gave us complete confidence in our investment.',
  },
  {
    name: 'Elizabeth M.',
    title: 'Jewelry Collector',
    description:
      "After twenty years of collecting fine jewelry, I can say with certainty that their diamond selection is among the finest I've encountered.",
  },
  {
    name: 'Michael C.',
    title: 'Chicago, IL',
    description:
      'What truly sets them apart is their unparalleled knowledge and personalized service. They transformed my vision into reality.',
  },
  {
    name: 'Sarah & David',
    title: 'Anniversary Celebration',
    description:
      'For our 10th anniversary, we wanted something extraordinary. Their custom design process created a piece that perfectly symbolizes our journey together.',
  },
  {
    name: 'Rebecca T.',
    title: 'Los Angeles, CA',
    description:
      'Incredible experience from start to finish. The diamond quality exceeded my expectations and the attention to detail was remarkable.',
  },
  {
    name: 'William P.',
    title: 'Luxury Watch Collector',
    description:
      'Their diamond selection process is methodical and precise. As someone who appreciates fine craftsmanship, I was thoroughly impressed.',
  },
];

function getHeaders() {
  return ['', 'Dream Diamonds', 'Traditional Retailers', 'Big U.S. Sites (for Canadians)'];
}

function getRows() {
  return [
    {
      aspect: 'Transparent Pricing',
      data: [
        { isPositive: true, title: 'Yes' },
        { isPositive: false, title: 'No' },
        { isPositive: false, title: 'No' },
      ],
    },
    {
      aspect: 'Free, Easy Returns',
      data: [
        { isPositive: true, title: '30-day free return' },
        { isPositive: false, title: 'Common restocking fees' },
        { isPositive: false, title: 'Return shipping & cross-border fees' },
      ],
    },
    {
      aspect: 'Live 360° Video & Gemologist Ratings',
      data: [
        { isPositive: true, title: 'Every diamond viewable + gemologist ratings' },
        { isPositive: false, title: 'Limited visuals' },
        { isPositive: false, title: 'Video often, but support is U.S.-centric' },
      ],
    },
    {
      aspect: 'Markups',
      data: [
        { isPositive: true, title: 'Low (Clear)' },
        { isPositive: false, title: 'High (Hidden)' },
        { isPositive: false, title: 'High (Hidden)' },
      ],
    },
    {
      aspect: 'Certified Natural Diamonds',
      data: [
        { isPositive: true, title: 'Yes' },
        { isPositive: true, title: 'Yes' },
        { isPositive: true, title: 'Yes' },
      ],
    },
  ];
}

const reviews: any = [
  {
    id: 1,
    name: 'Sarah M.',
    rating: 5,
    date: '2 weeks ago',
    title: 'Beautiful setting, exactly as described',
    content:
      'I was nervous about buying a setting online, but this exceeded my expectations.\nThe quality is outstanding and the craftsmanship is perfect.',
    verified: true,
  },
  {
    id: 2,
    name: 'Michael R.',
    rating: 5,
    date: '1 month ago',
    title: 'Great value and fast shipping',
    content:
      'Compared prices everywhere and this was the best deal. Shipping was fast and secure. The setting is gorgeous and my fiancée loves it.',
    verified: true,
  },
  {
    id: 3,
    name: 'Jennifer L.',
    rating: 5,
    date: '3 weeks ago',
    title: 'Excellent customer service',
    content:
      'Had some questions before purchasing and the customer service team was incredibly helpful. The setting arrived exactly as promised.',
    verified: true,
  },
  {
    id: 4,
    name: 'David K.',
    rating: 5,
    date: '5 days ago',
    title: 'High quality craftsmanship',
    content:
      'The attention to detail in this setting is remarkable. Very pleased with the purchase and would definitely recommend.',
    verified: true,
  },
  {
    id: 5,
    name: 'Emily R.',
    rating: 5,
    date: '1 week ago',
    title: 'Perfect for my vintage diamond',
    content:
      'This setting was exactly what I was looking for. The vintage style complements my diamond perfectly. Shipping was quick too.',
    verified: true,
  },
  {
    id: 6,
    name: 'Robert T.',
    rating: 5,
    date: '2 months ago',
    title: 'Outstanding quality and service',
    content:
      'From ordering to delivery, everything was seamless. The setting quality exceeded my expectations and my partner absolutely loves it.',
    verified: true,
  },
];

const HeroSection = () => {
  return (
    <div className="relative max-h-[calc(100vh-5rem)] overflow-hidden bg-gradient-to-r from-slate-800 via-[#0f172b] to-slate-900 text-white">
      <div className="absolute inset-y-0 right-0 w-full sm:w-2/3 lg:w-1/2">
        <Image
          src="/images/landing/landing-diamonds-3.jpg"
          alt="Premium Diamond Ring"
          fill
          className="object-cover opacity-20 sm:opacity-40"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-slate-900/60 to-slate-900 sm:via-slate-900/20" />
      </div>
      {/* Content */}
      <PageSectionLarge className="relative z-10 mx-auto max-w-7xl px-4">
        <div className="flex h-auto items-center lg:h-[calc(100vh-5rem)]">
          <div className="w-full max-w-3xl py-8 sm:py-12 md:py-24">
            <div className="mb-8">
              <TypographyHero className="mb-4">Exquisite Diamonds.</TypographyHero>
              <TypographyHero className="opacity-80">Transparent Pricing.</TypographyHero>
            </div>

            <div className="mb-12 max-w-2xl">
              <TypographyLarge>
                We show you everything - pricing, markups and everything else other jewelers don't
                want you to see. No hidden fees, no surprises.
              </TypographyLarge>
            </div>
            <div className="flex flex-col gap-4 sm:flex-row sm:gap-6">
              <SecondaryButtonMedium size="lg" asChild>
                <Link href="/diamonds">Shop Natural Diamonds</Link>
              </SecondaryButtonMedium>
              <SecondaryButtonMedium
                size="lg"
                variant="outline"
                className="text-foreground"
                asChild
              >
                <Link href="/lab-diamonds">Shop Lab Grown Diamonds</Link>
              </SecondaryButtonMedium>
            </div>
            <div className="mt-4">
              <TypographyLarge className="opacity-100">
                New here?{' '}
                <Link href="/quiz" className="underline">
                  Take our quiz
                </Link>
              </TypographyLarge>
            </div>
            <div className="mt-16 border-t pt-8">
              <TypographyText>Trusted by 50,000+ Couples Worldwide</TypographyText>
            </div>
          </div>
        </div>
      </PageSectionLarge>
    </div>
  );
};

export const ShopByShapeSection = () => {
  return (
    <PageSectionLarge className="mx-auto max-w-7xl px-4">
      <SectionHeader
        title="Explore Diamonds"
        subtitle="Each diamond shape offers unique characteristics and brilliance. Discover the perfect diamond cut that matches your style and personality."
      />
      <div className="mx-auto grid grid-cols-2 justify-items-center md:grid-cols-5 lg:grid-cols-10">
        {DEFAULT_SHAPES.slice(0, 10).map((shape, index) => (
          <Link
            key={index}
            href={`/diamonds/${shape.label.toLowerCase()}`}
            className="group flex cursor-pointer flex-col items-center transition-all duration-300 hover:scale-110"
          >
            <div className="mb-3 rounded-full p-3 shadow-lg">
              <DiamondIcon
                name={shape.name}
                size={40}
                className="sm:h-[50px] sm:w-[50px] lg:h-[60px] lg:w-[60px]"
              />
            </div>
            <TypographyLabel>{shape.label}</TypographyLabel>
          </Link>
        ))}
      </div>
    </PageSectionLarge>
  );
};

const BannerSection = () => {
  return (
    <>
      <PictureAndButton
        imageSrc="/images/landing/landing-diamonds-1.jpg"
        imageAlt="Brilliant Diamond Collection"
        title="Our Lab Grown Diamonds"
        description="Superior brilliance is in the cut. Discover the brightest diamonds in our collection."
        buttonText="Shop Lab Grown Diamonds"
        buttonVariant="outline"
        orientation="right"
        background="dark"
        className="my-6 mb-0 min-h-[30vh]"
        buttonHref="/lab-diamonds"
      />
      <PictureAndButton
        imageSrc="/images/landing/engagement-ring-hand.jpg"
        imageAlt="Diamond Certificate"
        title="Diamond Certificate"
        description="Every loose diamond that we sell receives expert grading for its cut, color, clarity, carat, and any elements of note."
        buttonText="Learn More"
        buttonVariant="default"
        orientation="left"
        background="light"
        className="my-6 mt-0 min-h-[30vh]"
        buttonHref="/education"
      />
    </>
  );
};

const InspiredSection = () => {
  return (
    <PageBackgroundFull className="py-12">
      {/* center the section and constrain to max-w-7xl */}
      <PageSectionLarge className="mx-auto max-w-7xl px-4">
        <SectionHeader title="Shop rings by style" />

        <div className="w-full">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
            {inspiredData.map((category, index) => (
              <Link key={index} href={category.href} className="block w-full">
                <SecondaryCard className="group flex h-full flex-col overflow-hidden rounded-lg border p-0 transition-transform duration-300 hover:scale-105">
                  <div className="relative flex h-44 items-center justify-center bg-gradient-to-b from-gray-100 to-white p-4 sm:h-56 lg:h-64">
                    <Image
                      src={category.image}
                      alt={category.title}
                      width={800}
                      height={800}
                      className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="pointer-events-none absolute inset-0" />
                  </div>

                  <div className="bg-white px-4 py-3 text-center">
                    <TypographyLabel className="text-primary text-sm font-semibold">
                      {category.title}
                    </TypographyLabel>
                  </div>
                </SecondaryCard>
              </Link>
            ))}
          </div>
        </div>
      </PageSectionLarge>
    </PageBackgroundFull>
  );
};

const ComparisonTable = () => {
  const renderCell = (
    { isPositive, title, description }: { isPositive: boolean; title: string; description: string },
    cellIndex: number,
  ) => (
    <td
      key={cellIndex}
      className={`w-1/4 p-2 align-top sm:p-4 ${cellIndex === 0 ? 'bg-slate-200/20' : ''}`}
    >
      <div className="flex flex-col items-start sm:flex-row sm:gap-2">
        <div className="mb-2 shrink-0 sm:mb-0">
          {isPositive ? (
            <Check className="h-4 w-4 text-green-500 sm:h-8 sm:w-8" />
          ) : (
            <X className="h-4 w-4 text-red-500 sm:h-8 sm:w-8" />
          )}
        </div>
        <div className="text-left">
          <TypographyLabel className="block sm:text-base">{title}</TypographyLabel>
          {description && (
            <TypographyText className="text-muted-foreground text-xs sm:text-base">
              {description}
            </TypographyText>
          )}
        </div>
      </div>
    </td>
  );

  return (
    <div className="overflow-x-auto rounded-lg border">
      <table className="w-full border-collapse overflow-hidden text-left text-xs sm:text-base">
        <thead>
          <tr className="border-b">
            {getHeaders().map((header, index) => (
              <th
                key={index}
                className={`p-2 font-semibold sm:p-4 ${index === 1 ? 'bg-gray-100/40' : ''}`}
              >
                <TypographyLabel className="font-semibold">{header}</TypographyLabel>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {getRows().map((row, rowIndex) => (
            <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-100/40' : 'bg-white'}>
              <td className="w-1/6 p-2 pr-1 align-top font-semibold sm:p-4">
                <TypographyLabel className="font-semibold">{row.aspect}</TypographyLabel>
              </td>
              {row.data.map((content, cellIndex) => renderCell(content, cellIndex))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const ComparisonSection = () => {
  return (
    <PageSectionLarge
      className="mx-auto max-w-7xl scroll-mt-12 px-4 drop-shadow-none lg:scroll-mt-14"
      id="comparison"
    >
      <SectionHeader
        title="What makes us different"
        subtitle="See how Dream Diamonds stands out in transparency, value and service compared to other retailers."
      />
      <ComparisonTable />
    </PageSectionLarge>
  );
};

export const ValuePropsSection = () => {
  return (
    <PageBackgroundFull className="bg-white py-12">
      <PageSectionLarge>
        <div className="container mx-auto max-w-full">
          <SectionHeader
            title="The Diamond Standard of Excellence"
            subtitle="Our commitment to exceptional quality and service defines every aspect of the experience we offer."
          />

          <div className="grid grid-cols-1 items-center gap-12 md:grid-cols-2">
            <div>
              <div className="mt-2 space-y-6">
                {featuresData.map(({ icon: Icon, title, description }, idx) => (
                  <div key={title} className="flex items-start gap-6 rounded-lg p-3">
                    <div className="from-primary to-primary ring-primary/10 mt-1 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-b text-white shadow-md ring-1">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div>
                      <TypographyLabel className="block text-lg font-semibold text-gray-900">
                        {title}
                      </TypographyLabel>
                      <TypographyText className="text-muted-foreground mt-1 max-w-md">
                        {description}
                      </TypographyText>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex h-full w-full justify-center md:justify-end">
              <Image
                src="/images/landing/engagement-ring-hand.jpg"
                alt="Preview"
                width={600}
                height={600}
                className="h-auto w-full max-w-md rounded-lg object-cover shadow-sm md:max-w-lg lg:max-w-xl"
              />
            </div>
          </div>
        </div>
      </PageSectionLarge>
    </PageBackgroundFull>
  );
};

export const Avatar = ({ name, bg }: { name: string; bg: string }) => (
  <div className={`h-10 w-10 ${bg} flex items-center justify-center rounded-full`}>
    <TypographySmall>
      {name
        .split(' ')
        .map((n) => n[0])
        .join('')}
    </TypographySmall>
  </div>
);

export const TestimonialsSection = () => {
  const sources = REVIEW_SOURCES;
  const avatarColors = AVATAR_COLORS;
  return (
    <PageSectionLarge className="mx-auto max-w-7xl px-4">
      <SectionHeader
        title="Client Testimonials"
        subtitle="Discover why discerning clients choose us for their most precious moments"
      />
      <div className="relative">
        <div className="mx-auto max-w-7xl">
          <Carousel opts={{ align: 'start', loop: false }}>
            <CarouselContent className="mx-0 my-1">
              {reviews.map((r, idx) => {
                const src = sources[idx % sources.length];
                return (
                  <CarouselItem key={r.id} className="mr-3 w-72 flex-shrink-0 basis-auto px-0">
                    <SecondaryCard className="p-4c">
                      <div className="mb-4 flex gap-4">
                        <Avatar name={r.name} bg={avatarColors[idx % avatarColors.length]} />
                        <div>
                          <TypographyText className="font-semibold">{r.name} </TypographyText>
                          <TypographySmall className="text-muted-foreground">
                            {r.date}
                          </TypographySmall>
                        </div>
                      </div>

                      <div className="mb-3 flex gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-4 w-4 ${
                              i < r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>

                      <div className="mb-3 flex-grow">
                        <TypographyText className="line-clamp-3 leading-relaxed">
                          "{r.content}"
                        </TypographyText>
                      </div>

                      <div className="mt-3 flex items-center justify-end">
                        {/* <div className="flex items-center gap-1">
                          {src === 'Google' ? (
                            <>
                              <div className="h-4 w-4 rounded-sm bg-gradient-to-r from-blue-500 to-green-500" />
                              <TypographySmall className="font-medium text-gray-600">
                                Google
                              </TypographySmall>
                            </>
                          ) : (
                            <>
                              <div className="h-4 w-4 rounded-sm bg-gradient-to-r from-green-400 to-green-600" />
                              <TypographySmall className="font-medium text-gray-600">
                                Trustpilot
                              </TypographySmall>
                            </>
                          )}
                        </div> */}
                      </div>
                    </SecondaryCard>
                  </CarouselItem>
                );
              })}
            </CarouselContent>

            {/* Buttons aligned to the container padding, non-blocking and visible on md+ */}
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center">
              <div className="pointer-events-auto hidden pl-4 md:flex">
                <CarouselPrevious variant="outline" size="icon" />
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
              <div className="pointer-events-auto hidden pr-4 md:flex">
                <CarouselNext variant="outline" size="icon" />
              </div>
            </div>
          </Carousel>
        </div>
      </div>
    </PageSectionLarge>
  );
};

const DiamondCarouselSection = () => {
  const [carouselApi, setCarouselApi] = useState<any>(null);
  const [canScrollPrev, setCanScrollPrev] = useState<boolean>(false);
  const [canScrollNext, setCanScrollNext] = useState<boolean>(false);
  const [currentSlide, setCurrentSlide] = useState<number>(0);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }
    const updateSelection = (): void => {
      setCanScrollPrev(carouselApi.canScrollPrev());
      setCanScrollNext(carouselApi.canScrollNext());
      setCurrentSlide(carouselApi.selectedScrollSnap());
    };
    updateSelection();
    carouselApi.on('select', updateSelection);
    return () => {
      carouselApi.off('select', updateSelection);
    };
  }, [carouselApi]);

  return (
    <PageSectionLarge className="mx-auto max-w-7xl px-4">
      <div className="flex flex-col items-center text-center">
        <SectionHeader
          title="Curated Collections"
          subtitle="Each exceptional piece in our collection is thoughtfully selected to exemplify the highest standards of craftsmanship and design elegance."
        />
      </div>
      <div className="relative w-full">
        <div className="relative mx-auto max-w-7xl">
          <Carousel
            setApi={setCarouselApi}
            opts={{
              align: 'start',
              loop: false,
              breakpoints: {
                '(max-width: 768px)': {
                  dragFree: true,
                },
              },
            }}
            className=""
          >
            <CarouselContent className="mx-0 -ml-4">
              {carouselData.map((item) => (
                <CarouselItem key={item.id} className="md:basis-1/2 lg:basis-1/3">
                  <a href={item.href} className="group block overflow-hidden">
                    <div className="group relative aspect-[4/5] overflow-hidden rounded-lg shadow-md transition-all duration-300 hover:shadow-xl">
                      <Image
                        src={item.image}
                        alt={item.title}
                        width={600}
                        height={750}
                        className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10" />
                      <div className="absolute inset-x-0 bottom-0 flex flex-col items-start p-8 text-white">
                        <div className="mb-2 h-px w-12 bg-white/60"></div>
                        <TypographySectionTitle className="mb-3">
                          {item.title}
                        </TypographySectionTitle>
                        <TypographyText className="mb-8 line-clamp-2 md:mb-1">
                          {item.description}
                        </TypographyText>
                        <div className="flex items-center">
                          <TypographyText>Explore</TypographyText>
                          <ArrowRight className="ml-3 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </div>
                      </div>
                    </div>
                  </a>
                </CarouselItem>
              ))}
            </CarouselContent>

            {/* Buttons aligned to the container padding, non-blocking and visible on md+ */}
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center">
              <div className="pointer-events-auto hidden pl-4 md:flex">
                <CarouselPrevious variant="outline" size="lg" />
              </div>
            </div>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center">
              <div className="pointer-events-auto hidden pr-4 md:flex">
                <CarouselNext variant="outline" size="lg" />
              </div>
            </div>
          </Carousel>
        </div>
        {/* Navigation dots: number of dots = slides - 2 */}
        <div className="mt-10 flex justify-center gap-3">
          {(() => {
            const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
            const totalDots = Math.max(1, isMobile ? carouselData.length : carouselData.length - 2);

            const activeDot = currentSlide >= totalDots ? totalDots - 1 : currentSlide;

            return Array.from({ length: totalDots }).map((_, index) => (
              <button
                key={index}
                className={`h-1.5 w-3 rounded-full transition-all ${
                  activeDot === index ? 'bg-gray-800' : 'bg-gray-300'
                }`}
                onClick={() => carouselApi?.scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
              />
            ));
          })()}
        </div>
      </div>
    </PageSectionLarge>
  );
};

const ExcellenceSection = () => {
  return (
    <PageSectionLarge className="mx-auto max-w-7xl px-4">
      <SectionHeader
        title="The Diamond Standard of Excellence"
        subtitle="Our commitment to exceptional quality and service defines every aspect of the experience we offer."
      />
      <div className="mt-12 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {featuresData.map((feature, index) => {
          const IconComponent = feature.icon;
          return (
            <SecondaryCard key={index} className="flex flex-col items-center space-y-4 p-8">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900">
                <IconComponent className="h-5 w-5 text-white" />
              </div>
              <TypographySectionLabel className="text-center">
                {feature.title}
              </TypographySectionLabel>
              <TypographyText className="text-center">{feature.description}</TypographyText>
            </SecondaryCard>
          );
        })}
      </div>
      <div className="mt-12 flex justify-center">
        <SecondaryButtonMedium size="lg">
          <Link href="/about">Discover our quality commitment</Link>
        </SecondaryButtonMedium>
      </div>
    </PageSectionLarge>
  );
};

export const CallToActionSection = () => {
  return (
    <PageBackgroundFull className="bg-muted relative mb-0">
      <div className="w-full py-16 sm:py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 text-center">
          <SectionHeader
            title="Discover Your Extraordinary Diamond"
            subtitle="Experience our curated collection of exceptional diamonds, each certified to meet the highest standards of excellence. Our private consultation service is available to guide your selection."
          />
          <div className="flex flex-col justify-center gap-4 sm:flex-row sm:gap-6 lg:gap-8">
            <SecondaryButtonMedium size="lg" asChild>
              <Link href="/">Explore collection</Link>
            </SecondaryButtonMedium>
          </div>
        </div>
      </div>
    </PageBackgroundFull>
  );
};

const Page = () => {
  const seoBase = {
    title: "Dream Diamonds | Canada's #1 Diamond Store",
    description:
      'Find your perfect diamond at Dream Diamonds. We offer a wide range of diamonds, settings, and services to help you find the perfect diamond for your needs.',
  };
  const siteUrl = process.env.NEXT_PUBLIC_CLIENT_URL;

  return (
    <>
      <NextSeo {...seoBase} />
      <SiteLinksSearchBoxJsonLd
        url={siteUrl}
        potentialActions={[
          {
            target: `${siteUrl}/diamonds/{search_term_string}`,
            queryInput: 'search_term_string',
          },
          {
            target: `${siteUrl}/rings/{search_term_string}`,
            queryInput: 'search_term_string',
          },
          {
            target: `${siteUrl}/education/{search_term_string}`,
            queryInput: 'search_term_string',
          },
        ]}
      />
      <PageContainer className="space-y-8">
        <HeroSection />
        <ShopByShapeSection />
        <BannerSection />
        <InspiredSection />
        <ComparisonSection />
        <ValuePropsSection />
        <DiamondCarouselSection />
        <TestimonialsSection />
        {/* <ExcellenceSection /> */}
        <IndexFAQSection />
        <CallToActionSection />
      </PageContainer>
    </>
  );
};
export default Page;
